"use client"
import React, { useEffect, useRef } from 'react'
import { motion, useMotionValue, useSpring, useTransform } from 'motion/react'

interface ParallaxHeroProps {
    /** 游戏封面地址（已 encodeURI） */
    src: string
}

/**
 * gamedetail2 Hero 封面虚化背景：随鼠标轻微视差（横 ≤12px / 纵 ≤8px）
 * 监听 window mousemove，相对 Hero 区域中心归一化到 [-1,1]，spring 平滑跟随；
 * 图片本身 scale-110，位移不会露出边缘
 */
export default function ParallaxHero({ src }: ParallaxHeroProps) {
    const ref = useRef<HTMLDivElement>(null)
    const mx = useMotionValue(0)
    const my = useMotionValue(0)
    const sx = useSpring(mx, { stiffness: 50, damping: 18 })
    const sy = useSpring(my, { stiffness: 50, damping: 18 })
    const x = useTransform(sx, (v) => v * 12)
    const y = useTransform(sy, (v) => v * 8)

    useEffect(() => {
        const onMove = (e: MouseEvent) => {
            const el = ref.current
            if (!el) return
            const r = el.getBoundingClientRect()
            const nx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2)
            const ny = (e.clientY - (r.top + r.height / 2)) / (r.height / 2)
            mx.set(Math.max(-1, Math.min(1, nx)))
            my.set(Math.max(-1, Math.min(1, ny)))
        }
        window.addEventListener('mousemove', onMove, { passive: true })
        return () => window.removeEventListener('mousemove', onMove)
    }, [mx, my])

    return (
        <div ref={ref} className="pointer-events-none absolute inset-0">
            <motion.img
                src={src}
                alt=""
                style={{ x, y }}
                className="h-full w-full object-cover opacity-30 blur-2xl scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/85 to-slate-950" />
        </div>
    )
}
